import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../api/client';

export type BusinessConfigRef = {
  config_id: string;
  name?: string;
  access_level?: 'viewer' | 'editor' | 'admin';
};

export type BusinessEntityRef = {
  entity_id: string;
  name: string;
  type?: string;
  configs: BusinessConfigRef[];
};

export type BusinessTenantRef = {
  tenant_id: string;
  name: string;
  role?: { role_id: string; name: string };
  entities: BusinessEntityRef[];
};

type AuthMeResponse = {
  user: {
    id: number;
    username: string;
    displayName?: string;
    role?: string;
  };
  tenants: BusinessTenantRef[];
};

type BusinessContextType = {
  loading: boolean;
  tenants: BusinessTenantRef[];
  availableWebAdapterConfigs: Array<{
    config_id: string;
    name: string;
  }>;
  activeTenantId: string | null;
  activeEntityId: string | null;
  activeConfigId: string | null;
  setActiveTenantId: (tenantId: string | null) => void;
  setActiveEntityId: (entityId: string | null) => void;
  setActiveConfigId: (configId: string | null) => void;
  setActiveConfigIdSafe: (configId: string | null, locked?: boolean) => boolean;
  refresh: () => Promise<void>;
};

const STORAGE_KEY = 'intellicare_business_context';

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, access } = useAuth();

  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<BusinessTenantRef[]>([]);
  const [configCatalog, setConfigCatalog] = useState<any[]>([]);

  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [activeEntityId, setActiveEntityId] = useState<string | null>(null);
  const [activeConfigId, setActiveConfigId] = useState<string | null>(null);

  const userSetEntityIdRef = useRef<string | null>(null);
  const userSetConfigIdRef = useRef<string | null>(null);

  const availableWebAdapterConfigs = useMemo(() => {
    const byId = new Map<string, string>();
    const raw = Array.isArray(configCatalog) ? configCatalog : [];

    for (const cfg of raw) {
      const id =
        cfg?.config_id ??
        cfg?.id ??
        cfg?.configId ??
        cfg?.pk;

      const name =
        cfg?.name ??
        cfg?.config_name ??
        cfg?.title ??
        String(id ?? '');

      if (!id) continue;
      byId.set(String(id), String(name));
    }

    return Array.from(byId.entries()).map(([config_id, name]) => ({ config_id, name }));
  }, [configCatalog]);

  const setActiveEntityIdByUser = useCallback((entityId: string | null) => {
    userSetEntityIdRef.current = entityId;
    setActiveEntityId(entityId);
  }, []);

  const setActiveConfigIdByUser = useCallback((configId: string | null) => {
    userSetConfigIdRef.current = configId;
    setActiveConfigId(configId);
  }, []);

  const setActiveConfigIdSafe = useCallback(
    (configId: string | null, locked = false) => {
      if (locked) return false;
      setActiveConfigIdByUser(configId);
      return true;
    },
    [setActiveConfigIdByUser],
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as {
        activeTenantId?: string | null;
        activeEntityId?: string | null;
        activeConfigId?: string | null;
      };
      setActiveTenantId(data.activeTenantId ?? null);
      setActiveEntityId(data.activeEntityId ?? null);
      setActiveConfigId(data.activeConfigId ?? null);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ activeTenantId, activeEntityId, activeConfigId }),
      );
    } catch {
      // ignore
    }
  }, [activeTenantId, activeEntityId, activeConfigId]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setTenants([]);
      setConfigCatalog([]);
      return;
    }

    setLoading(true);

    try {
      const [meResp, cfgResp] = await Promise.all([
        apiFetch('/api/auth/me', { method: 'GET' }),
        apiFetch('/dhis2-configs/', { method: 'GET' }),
      ]);

      if (!meResp.ok) {
        setTenants([]);
        setConfigCatalog([]);
        return;
      }

      const data = (await meResp.json()) as AuthMeResponse;
      const t = Array.isArray(data?.tenants) ? data.tenants : [];
      setTenants(t);

      if (cfgResp.ok) {
        const cfgData = await cfgResp.json().catch(() => []);
        if (Array.isArray(cfgData)) {
          setConfigCatalog(cfgData);
        } else if (cfgData && Array.isArray(cfgData.results)) {
          setConfigCatalog(cfgData.results);
        } else if (cfgData && Array.isArray(cfgData.data)) {
          setConfigCatalog(cfgData.data);
        } else {
          setConfigCatalog([]);
        }
      } else {
        setConfigCatalog([]);
      }
    } catch (error) {
      console.error('BusinessContext refresh failed:', error);
      setTenants([]);
      setConfigCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const tenantIds = new Set(tenants.map((x) => x.tenant_id));
    const resolvedTenantId =
      activeTenantId && tenantIds.has(activeTenantId)
        ? activeTenantId
        : (tenants[0]?.tenant_id ?? null);

    const resolvedTenant = tenants.find((x) => x.tenant_id === resolvedTenantId) ?? null;

    const entityIds = new Set((resolvedTenant?.entities ?? []).map((e) => e.entity_id));
    let resolvedEntityId =
      activeEntityId && entityIds.has(activeEntityId)
        ? activeEntityId
        : (resolvedTenant?.entities?.[0]?.entity_id ?? null);

    if (userSetEntityIdRef.current && activeEntityId === userSetEntityIdRef.current) {
      resolvedEntityId = activeEntityId;
      userSetEntityIdRef.current = null;
    }

    const resolvedEntity =
      (resolvedTenant?.entities ?? []).find((e) => e.entity_id === resolvedEntityId) ?? null;

    const entityConfigList = resolvedEntity?.configs ?? [];
    const configIds = new Set(entityConfigList.map((c) => c.config_id));
    const firstConfig = entityConfigList[0] ?? null;

    let resolvedConfigId =
      configIds.size > 0
        ? activeConfigId && configIds.has(activeConfigId)
          ? activeConfigId
          : (firstConfig?.config_id ?? null)
        : activeConfigId;

    if (userSetConfigIdRef.current && activeConfigId === userSetConfigIdRef.current) {
      resolvedConfigId = activeConfigId;
      userSetConfigIdRef.current = null;
    }

    if (access?.config_scope_restricted && access.allowed_config_ids?.length) {
      const allowed = new Set(access.allowed_config_ids);
      if (!resolvedConfigId || !allowed.has(resolvedConfigId)) {
        resolvedConfigId = access.allowed_config_ids[0];
      }
    }

    if (resolvedTenantId !== activeTenantId) setActiveTenantId(resolvedTenantId);
    if (resolvedEntityId !== activeEntityId) setActiveEntityIdByUser(resolvedEntityId);
    if (resolvedConfigId !== activeConfigId) setActiveConfigIdByUser(resolvedConfigId);
  }, [
    tenants,
    activeTenantId,
    activeEntityId,
    activeConfigId,
    access,
    setActiveEntityIdByUser,
    setActiveConfigIdByUser,
  ]);

  const value = useMemo<BusinessContextType>(
    () => ({
      loading,
      tenants,
      availableWebAdapterConfigs,
      activeTenantId,
      activeEntityId,
      activeConfigId,
      setActiveTenantId,
      setActiveEntityId: setActiveEntityIdByUser,
      setActiveConfigId: setActiveConfigIdByUser,
      setActiveConfigIdSafe,
      refresh,
    }),
    [
      loading,
      tenants,
      availableWebAdapterConfigs,
      activeTenantId,
      activeEntityId,
      activeConfigId,
      refresh,
      setActiveEntityIdByUser,
      setActiveConfigIdByUser,
      setActiveConfigIdSafe,
    ],
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusiness() {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used within BusinessProvider');
  return ctx;
}
